import React, { useEffect, useState, useCallback } from "react";

/**
 * PWAInstallPrompt — floating teal "INSTALL APP" CTA visible on mobile
 * once the browser fires `beforeinstallprompt`. Tapping it opens the
 * native install sheet.
 *
 * Behaviour
 * ---------
 *   • Chromium-based browsers (Android Chrome, Edge, Brave, Samsung
 *     Internet, desktop Chromium) supply `beforeinstallprompt` so we
 *     show the CTA whenever the event has fired and the app isn't
 *     already installed.
 *   • iOS Safari does NOT fire that event — there is no programmatic
 *     install API. For iOS we render a discreet bubble explaining the
 *     manual Share → "Add to Home Screen" flow the FIRST time a user
 *     visits on iPhone/iPad. (Capped to 1 impression / 7 days via
 *     localStorage.)
 *   • A user dismissal is honoured for 14 days (Android) / 30 days
 *     (iOS guide) so we don't nag.
 *
 * Tested with: data-testid="pwa-install-cta", "pwa-install-dismiss",
 *              "pwa-install-ios-bubble", "pwa-install-ios-dismiss".
 */
const DISMISS_KEY = "lhub_pwa_install_dismissed_until";
const IOS_DISMISS_KEY = "lhub_pwa_ios_dismissed_until";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Modern iPad-Safari reports as Mac in UA — also catch maxTouchPoints > 1.
  return (/iPad|iPhone|iPod/.test(ua) && !window.MSStream)
      || (ua.includes("Macintosh") && (navigator.maxTouchPoints || 0) > 1);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari quirk
  return !!window.navigator.standalone;
}

function dismissedUntilNow(key) {
  try {
    const until = parseInt(window.localStorage.getItem(key) || "0", 10);
    return Number.isFinite(until) && until > Date.now();
  } catch (_e) { return false; }
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;

    // Chromium path: capture beforeinstallprompt so we can fire it on
    // the user's tap (browsers reject programmatic calls outside a
    // user-gesture handler).
    const onPrompt = (e) => {
      e.preventDefault();
      if (dismissedUntilNow(DISMISS_KEY)) return;
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // Hide our UI as soon as the user finishes the native install flow.
    const onInstalled = () => {
      setDeferredPrompt(null);
      setShowIOSGuide(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS path: render the one-time "tap Share → Add to Home Screen"
    // bubble. We only do this on devices that actually look like iOS
    // and only once per 30 days.
    if (isIOS() && !dismissedUntilNow(IOS_DISMISS_KEY)) {
      // Give the first paint room to land before nagging.
      const t = setTimeout(() => setShowIOSGuide(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onPrompt);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === "dismissed") {
        try {
          window.localStorage.setItem(
            DISMISS_KEY,
            String(Date.now() + 14 * 24 * 60 * 60 * 1000),
          );
        } catch (_e) { /* ignore */ }
      }
    } catch (_e) { /* user-cancel is fine */ }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + 14 * 24 * 60 * 60 * 1000),
      );
    } catch (_e) { /* ignore */ }
    setDeferredPrompt(null);
  }, []);

  const dismissIOS = useCallback(() => {
    try {
      window.localStorage.setItem(
        IOS_DISMISS_KEY,
        String(Date.now() + 30 * 24 * 60 * 60 * 1000),
      );
    } catch (_e) { /* ignore */ }
    setShowIOSGuide(false);
  }, []);

  // Chromium banner
  if (deferredPrompt) {
    return (
      <div
        data-testid="pwa-install-banner"
        // bottom-20 keeps the banner above the MobileBottomNav (h=64ish + safe-area).
        className="fixed left-3 right-3 bottom-20 lg:left-auto lg:right-6 lg:bottom-6 lg:max-w-sm z-[55] rounded-2xl border border-[#14b8a6]/60 bg-[#0c0e10]/95 backdrop-blur-xl shadow-[0_0_30px_rgba(20,184,166,0.35)] p-4 flex items-center gap-3 lhub-fade-in"
      >
        <div className="w-11 h-11 shrink-0 rounded-2xl bg-[#14b8a6]/15 border border-[#14b8a6]/50 flex items-center justify-center text-[#14b8a6]">
          <i className="fas fa-mobile-screen-button text-lg" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] tracking-[3px] font-bold text-[#14b8a6]">INSTALL APP</p>
          <p className="text-[12px] text-white/85 leading-snug truncate">
            Add LEAGUE HUB to your home screen — faster launch, offline shell.
          </p>
        </div>
        <button
          type="button"
          onClick={install}
          data-testid="pwa-install-cta"
          className="shrink-0 px-3 py-2 rounded-xl bg-[#14b8a6] text-black text-[11px] font-bold tracking-widest hover:scale-[1.03] active:scale-[0.99] transition-transform"
        >
          INSTALL
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss install prompt"
          className="shrink-0 w-8 h-8 rounded-lg text-white/50 hover:text-white hover:bg-white/5 inline-flex items-center justify-center"
        >
          <i className="fas fa-times" />
        </button>
      </div>
    );
  }

  // iOS guide bubble (no programmatic install on iOS Safari)
  if (showIOSGuide) {
    return (
      <div
        data-testid="pwa-install-ios-bubble"
        className="fixed left-3 right-3 bottom-20 z-[55] rounded-2xl border border-[#14b8a6]/55 bg-[#0c0e10]/95 backdrop-blur-xl shadow-[0_0_30px_rgba(20,184,166,0.30)] p-4 lhub-fade-in"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-2xl bg-[#14b8a6]/15 border border-[#14b8a6]/50 flex items-center justify-center text-[#14b8a6]">
            <i className="fas fa-mobile-screen-button" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-[3px] font-bold text-[#14b8a6]">INSTALL ON IPHONE</p>
            <p className="text-[12px] text-white/85 leading-snug mt-1">
              Tap <i className="fas fa-arrow-up-from-bracket text-[#14b8a6] mx-0.5" /> Share, then
              <span className="font-bold text-white"> Add to Home Screen</span> for full-screen
              LEAGUE HUB on your phone.
            </p>
          </div>
          <button
            type="button"
            onClick={dismissIOS}
            data-testid="pwa-install-ios-dismiss"
            aria-label="Dismiss iOS install guide"
            className="shrink-0 w-8 h-8 rounded-lg text-white/50 hover:text-white hover:bg-white/5 inline-flex items-center justify-center"
          >
            <i className="fas fa-times" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}

export default PWAInstallPrompt;
